/**
 * Ambient audio (M13) — fully procedural WebAudio; there are no audio
 * assets in the kit. Three layers:
 *
 * - PAD: a mood-tinted drone chord (few detuned sines through a lowpass)
 *   that crossfades when the atmosphere zone changes — the ear hears the
 *   climate turn over with the sky.
 * - ROLL: a filtered-noise bed whose gain/brightness follow marble speed
 *   — the marble's voice; silent at rest, a bright rush at full tilt.
 * - CUES: one-shot envelope sweeps for events (boost entry, launch,
 *   landing, fall/respawn, milestone) — rule-derived, no RNG.
 *
 * The mapping layer (padForMood / rollParams / cueFor) is PURE and rule-
 * tested headlessly; the WebAudio layer is a thin realization. Autoplay
 * policy: everything is muted until the user toggles audio on (the M key
 * or the HUD button) — the toggle gesture is what may create/resume the
 * AudioContext.
 */

// ---------------------------------------------------------------------------
// Pure mapping layer
// ---------------------------------------------------------------------------

export interface PadSpec {
  /** Chord partials, Hz, ascending — the mood's drone. */
  freqs: readonly number[];
  /** Pad lowpass cutoff, Hz (dark moods sit low). */
  cutoff: number;
  /** Pad layer gain, 0..1 (scaled by the master). */
  gain: number;
  /** Air/wind noise lowpass cutoff, Hz. */
  airCutoff: number;
  /** Air layer gain, 0..1. */
  airGain: number;
}

/** Mood → drone spec. Chords stay low and open (screensaver-grade: felt,
 * not listened to). Every atmosphere mood has a row — a missing row is a
 * test failure, not a silent default. */
const PADS: Record<string, PadSpec> = {
  // Low minor-ish drone, dark and calm.
  midnight: {
    freqs: [73.42, 110.0, 174.61, 220.0],
    cutoff: 420,
    gain: 0.5,
    airCutoff: 500,
    airGain: 0.14,
  },
  // Warm low third + crackle-bright air.
  ember: {
    freqs: [82.41, 103.83, 164.81],
    cutoff: 380,
    gain: 0.55,
    airCutoff: 1400,
    airGain: 0.2,
  },
  // Suspended shimmer, brighter cutoff.
  violet: {
    freqs: [87.31, 130.81, 196.0, 261.63],
    cutoff: 700,
    gain: 0.45,
    airCutoff: 900,
    airGain: 0.16,
  },
  // A deep fifth, nearly subsonic — pressure more than pitch.
  abyss: {
    freqs: [55.0, 82.41, 110.0],
    cutoff: 260,
    gain: 0.6,
    airCutoff: 350,
    airGain: 0.1,
  },
  // Airy add9, the air layer leads (wind over snow).
  snowdrift: {
    freqs: [98.0, 146.83, 220.0, 293.66],
    cutoff: 850,
    gain: 0.4,
    airCutoff: 2200,
    airGain: 0.26,
  },
  // Warm major sixth, golden and settled.
  goldhour: {
    freqs: [98.0, 123.47, 164.81, 196.0],
    cutoff: 560,
    gain: 0.5,
    airCutoff: 750,
    airGain: 0.15,
  },
};

export function padForMood(mood: string): PadSpec | null {
  return PADS[mood] ?? null;
}

export interface RollSpec {
  /** Roll-noise gain, 0..1 — silent at rest. */
  gain: number;
  /** Bandpass center, Hz — brighter with speed. */
  cutoff: number;
}

/** Marble speed (world u/s) → rolling-noise voice. Monotone in speed,
 * bounded: gain 0→0.4 saturating ~8 u/s, cutoff 240→2200 Hz. */
export function rollParams(speed: number): RollSpec {
  const s = Math.max(0, speed);
  const t = 1 - Math.exp(-s / 3.2);
  return {
    gain: 0.4 * t,
    cutoff: 240 + 1960 * t,
  };
}

export type CueKind =
  | "boost"
  | "launch"
  | "land"
  | "fall"
  | "milestone"
  | "checkpoint";

export interface CueSpec {
  /** Sweep start/end frequency, Hz. */
  freq0: number;
  freq1: number;
  /** Envelope length, seconds. */
  dur: number;
  /** Peak gain, 0..0.5 (cues sit above the bed, below discomfort). */
  gain: number;
  type: "sine" | "triangle";
}

const CUES: Record<CueKind, CueSpec> = {
  // Rising surge into a boost/gravity zone.
  boost: { freq0: 220, freq1: 560, dur: 0.35, gain: 0.18, type: "triangle" },
  // Lip launch — a quick upward zip.
  launch: { freq0: 330, freq1: 990, dur: 0.25, gain: 0.2, type: "triangle" },
  // Caught — a soft settling thump.
  land: { freq0: 260, freq1: 130, dur: 0.22, gain: 0.22, type: "sine" },
  // Falling off the line — a long descend into the respawn.
  fall: { freq0: 520, freq1: 90, dur: 0.7, gain: 0.24, type: "sine" },
  // Distance milestone — a bright little bell rise.
  milestone: { freq0: 660, freq1: 880, dur: 0.5, gain: 0.2, type: "sine" },
  // Checkpoint/segment crossing tick (subtle).
  checkpoint: { freq0: 440, freq1: 440, dur: 0.08, gain: 0.08, type: "sine" },
};

export function cueFor(kind: CueKind): CueSpec {
  return CUES[kind];
}

// ---------------------------------------------------------------------------
// WebAudio realization (browser only — nothing below is imported by tests)
// ---------------------------------------------------------------------------

const MASTER_GAIN = 0.5;
/** Pad/mood crossfade, seconds (matches the 4s sky crossfade). */
const PAD_FADE = 4;

export class AmbientAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private padNodes: { osc: OscillatorNode[]; gain: GainNode } | null = null;
  private airNodes: { gain: GainNode; filter: BiquadFilterNode } | null = null;
  private rollNodes: { gain: GainNode; filter: BiquadFilterNode } | null = null;
  private mood = "midnight";
  enabled = false;

  /** Toggle from a user gesture — the only place the context is made. */
  toggle(): boolean {
    this.enabled = !this.enabled;
    if (this.enabled) {
      this.ensure();
      this.ctx?.resume();
      this.master?.gain.setTargetAtTime(MASTER_GAIN, this.now(), 0.4);
    } else {
      this.master?.gain.setTargetAtTime(0, this.now(), 0.15);
    }
    return this.enabled;
  }

  /** Atmosphere zone turned over — crossfade the pad + air to the mood. */
  setMood(mood: string): void {
    this.mood = mood;
    if (!this.ctx) return;
    const spec = padForMood(mood);
    if (!spec) return;
    this.buildPad(spec);
  }

  /** Per-frame: the marble's rolling voice follows its speed. */
  setSpeed(speed: number): void {
    if (!this.ctx || !this.rollNodes || !this.enabled) return;
    const { gain, cutoff } = rollParams(speed);
    this.rollNodes.gain.gain.setTargetAtTime(gain, this.now(), 0.12);
    this.rollNodes.filter.frequency.setTargetAtTime(cutoff, this.now(), 0.12);
  }

  /** One-shot event cue. */
  cue(kind: CueKind): void {
    if (!this.ctx || !this.master || !this.enabled) return;
    const spec = cueFor(kind);
    const t = this.now();
    const osc = this.ctx.createOscillator();
    osc.type = spec.type;
    osc.frequency.setValueAtTime(spec.freq0, t);
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(30, spec.freq1),
      t + spec.dur,
    );
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(spec.gain, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + spec.dur);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + spec.dur + 0.05);
    osc.onended = () => {
      osc.disconnect();
      g.disconnect();
    };
  }

  dispose(): void {
    this.ctx?.close();
    this.ctx = null;
    this.master = null;
    this.padNodes = null;
    this.airNodes = null;
    this.rollNodes = null;
  }

  private now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  private ensure(): void {
    if (this.ctx) return;
    const Ctor = (globalThis as { AudioContext?: typeof AudioContext })
      .AudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(this.ctx.destination);

    // Shared looping noise buffer (2s of white noise).
    const noise = this.ctx.createBuffer(
      1,
      this.ctx.sampleRate * 2,
      this.ctx.sampleRate,
    );
    const data = noise.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

    // AIR: lowpassed noise, mood-tinted.
    {
      const src = this.ctx.createBufferSource();
      src.buffer = noise;
      src.loop = true;
      const filter = this.ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 600;
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      src.connect(filter).connect(gain).connect(this.master);
      src.start();
      this.airNodes = { gain, filter };
    }
    // ROLL: bandpassed noise following speed.
    {
      const src = this.ctx.createBufferSource();
      src.buffer = noise;
      src.loop = true;
      const filter = this.ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 400;
      filter.Q.value = 0.8;
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      src.connect(filter).connect(gain).connect(this.master);
      src.start();
      this.rollNodes = { gain, filter };
    }
    this.buildPad(padForMood(this.mood) ?? PADS.midnight);
  }

  private buildPad(spec: PadSpec): void {
    if (!this.ctx || !this.master) return;
    const t = this.now();
    // Fade the old pad out and schedule its teardown.
    if (this.padNodes) {
      const old = this.padNodes;
      old.gain.gain.setTargetAtTime(0, t, PAD_FADE / 4);
      setTimeout(() => {
        for (const o of old.osc) {
          try {
            o.stop();
            o.disconnect();
          } catch {
            // already stopped
          }
        }
        old.gain.disconnect();
      }, PAD_FADE * 1000);
    }
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = spec.cutoff;
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(spec.gain, t, PAD_FADE / 4);
    filter.connect(gain).connect(this.master);
    const oscs = spec.freqs.map((f, i) => {
      const osc = this.ctx!.createOscillator();
      osc.type = "sine";
      // Slight detune spread keeps the drone alive without an LFO.
      osc.frequency.value = f * (1 + (i % 2 === 0 ? 0.0012 : -0.0012));
      osc.connect(filter);
      osc.start();
      return osc;
    });
    this.padNodes = { osc: oscs, gain };
    // Air layer follows the mood too.
    if (this.airNodes) {
      this.airNodes.filter.frequency.setTargetAtTime(
        spec.airCutoff,
        t,
        PAD_FADE / 4,
      );
      this.airNodes.gain.gain.setTargetAtTime(spec.airGain, t, PAD_FADE / 4);
    }
  }
}
